import { CalendarPlus } from "lucide-react";
import { useState } from "react";

import { DateField } from "@/components/DateField";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { frDate, today } from "@/components/pipeline/utils";
import { HOURS } from "@/components/pipeline/lead/OutcomeBar";

const DAY_PART = [[/apr[eè]s[- ]?midi|aprem/i, "14:00"], [/matin/i, "09:00"], [/midi/i, "12:00"], [/soir/i, "18:00"]];
// Disponibilité notée dans un commentaire récent (« disponible le matin à partir de 9h ») : proposée dans Planifier
export const availability = (comments) => {
  for (const c of comments || []) {
    const m = (c.content || "").match(/(?:disponible|dispo|joignable|[àa] rappeler|rappeler|appeler)[^.\n]{0,80}/i);
    if (!m) continue;
    // heure de joignabilité (7 h - 20 h) ; « sous 48h », « dans 2h » sont des délais, pas des heures
    const h = [...m[0].matchAll(/(sous|dans|d'ici)?\s*(\d{1,2})\s*h\s*(\d{2})?/gi)].find((x) => !x[1] && Number(x[2]) >= 7 && Number(x[2]) <= 20);
    const near = h && HOURS.reduce((best, x) => (Math.abs(Number(x.slice(0, 2)) - Number(h[2])) < Math.abs(Number(best.slice(0, 2)) - Number(h[2])) ? x : best));
    const time = near || DAY_PART.find(([re]) => re.test(m[0]))?.[1];
    if (time || /lundi|mardi|mercredi|jeudi|vendredi|semaine/i.test(m[0])) return { text: m[0].trim(), time };
  }
  return null;
};

const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const QUICK = [["Demain", 1], ["Dans 2 j", 2], ["Dans 1 sem.", 7], ["Dans 2 sem.", 14], ["Dans 1 mois", 30]];
const dayDiff = (iso) => Math.round((new Date(`${iso}T00:00:00`) - new Date(`${today()}T00:00:00`)) / 86400000);

export const remindState = (lead) => {
  if (!["todo", "standby"].includes(lead.status)) return { tone: "plain", text: "Fiche clôturée" };
  if (!lead.remind_date) return { tone: "tomato", text: "Aucune prochaine action" };
  const d = dayDiff(lead.remind_date);
  if (d < 0) return { tone: "tomato", text: `En retard de ${-d} j` };
  if (d === 0) return { tone: "butter", text: "Aujourd'hui" };
  return { tone: "pine", text: `Dans ${d} j` };
};

// Règle d'or du plateau : jamais de fiche sans prochaine action
export const NextAction = ({ lead, suggestion, hint, best, saving, onPlan, open, setOpen }) => {
  const [note, setNote] = useState("");
  const [time, setTime] = useState(hint?.time || best?.time || "");
  const st = remindState(lead);
  const plan = (date) => { onPlan(date, note.trim(), time || null); setNote(""); setOpen(false); };
  return (
    <div className={`ldx-next ldx-next--${st.tone}`} data-testid="lead-next-action">
      <span className="ldx-k">Prochaine action</span>
      <div className="ldx-next__row">
        <div className="ldx-next__txt">
          <b data-testid="lead-next-date">{lead.remind_date ? `Relance ${frDate(lead.remind_date)}${lead.remind_time ? ` · ${lead.remind_time}` : ""}` : "À planifier"}</b>
          <small data-testid="lead-next-state">{st.text}</small>
        </div>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" className="btn btn--sm btn--ink ldx-plan" disabled={saving} data-testid="lead-plan-btn"><CalendarPlus size={15} /> Planifier <kbd>P</kbd></button>
          </PopoverTrigger>
          <PopoverContent align="end" className="ldx-plan-pop" data-testid="lead-plan-popover">
            <strong>Planifier la prochaine action</strong>
            {suggestion && <p className="ldx-sugg" data-testid="lead-plan-suggestion">Conseil IA : {suggestion}</p>}
            {hint && <p className="ldx-sugg ldx-sugg--hint" data-testid="lead-plan-availability">Disponibilité notée : « {hint.text} »</p>}
            {best && <p className="ldx-sugg ldx-sugg--hint" data-testid="lead-plan-best-hour">Meilleure heure : {best.time.replace(":00", " h")} ({best.reason})</p>}
            <input className="ldx-plan-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Objet (facultatif) : rappeler le maire, envoyer le devis…" data-testid="lead-plan-note" />
            <div className="ldx-quick" data-testid="lead-plan-hours"><span className="ldx-k">Heure</span>{HOURS.map((h) => <button key={h} type="button" className="chip" aria-pressed={time === h} onClick={() => setTime(time === h ? "" : h)} data-testid={`lead-plan-hour-${h.slice(0, 2)}`}>{h.replace(":00", " h")}</button>)}</div>
            <div className="ldx-quick">{QUICK.map(([l, n]) => <button key={n} type="button" className="chip" onClick={() => plan(addDays(n))} data-testid={`lead-plan-quick-${n}`}>{l}</button>)}</div>
            <DateField min={today()} value="" clearable={false} onChange={(v) => v && plan(v)} placeholder="Autre date…" testid="lead-plan-date" />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};
